import { toast } from 'sonner';
import i18next from 'i18next';
import { useCurrentSituationEvaluationStore } from '@/store/current-situation-evaluation.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class CurrentSituationEvaluationIntegration {
    constructor(scopeService, applicabilityService) {
        this.scopeService = scopeService;
        this.applicabilityService = applicabilityService;
    }

    _getErrorMessage(error, fallbackKey, fallbackDefault) {
        return getIntegrationErrorMessage(error);
    }

    _normalizeList(payload) {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    }

    async fetchScope() {
        const store = useCurrentSituationEvaluationStore.getState();
        store.setScopeLoading(true);

        try {
            const response = await this.scopeService.getScope();
            const data = response.data || {};
            store.setScopeData({
                applicabilityIds: data.applicabilityIds || [],
                options: data.options || [],
            });
            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationEvaluation:messages.fetchScopeError',
                'Kapsam bilgisi yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setScopeLoading(false);
        }
    }

    async saveScope(applicabilityIds) {
        const store = useCurrentSituationEvaluationStore.getState();
        store.setScopeSaving(true);

        try {
            const response = await this.scopeService.updateScope({ applicabilityIds });
            const savedIds = response.data?.applicabilityIds || applicabilityIds;
            store.setScopeApplicabilityIds(savedIds);
            toast.success(
                i18next.t('bigdesCurrentSituationEvaluation:messages.saveScopeSuccess', {
                    defaultValue: 'Scope saved',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationEvaluation:messages.saveScopeError',
                'Kapsam kaydedilemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setScopeSaving(false);
        }
    }

    async fetchAll(params = {}) {
        const store = useCurrentSituationEvaluationStore.getState();
        store.setLoading(true);
        store.clearError();

        try {
            const merged = { ...store.searchParams, ...params };
            const response = await this.applicabilityService.getAll({
                page: merged.page,
                pageSize: merged.pageSize,
                search: merged.search || undefined,
                sortBy: merged.sortBy,
                sortOrder: merged.sortOrder,
                context: 'currentSituationEvaluation',
            });

            const rows = this._normalizeList(response.data);
            store.setItems(rows);
            if (response.pagination) {
                store.setPaginationData({
                    total: response.pagination.total ?? 0,
                    totalPages: response.pagination.totalPages ?? 1,
                    page: response.pagination.page ?? merged.page,
                    pageSize: response.pagination.pageSize ?? merged.pageSize,
                });
            }

            return { success: true, data: rows, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationEvaluation:messages.fetchError',
                'Mevcut durum değerlendirme listesi yüklenemedi',
            );
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateMeasureEvaluation(applicabilityId, measureId, payload) {
        try {
            const response = await this.applicabilityService.updateCurrentSituationMeasureEvaluation(
                applicabilityId,
                measureId,
                payload,
            );
            toast.success(
                i18next.t('bigdesCurrentSituationEvaluation:messages.updateEvaluationSuccess', {
                    defaultValue: 'Current situation evaluation saved',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationEvaluation:messages.updateEvaluationError',
                'Mevcut durum değerlendirmesi kaydedilemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
